import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { nameof } from "ts-simple-nameof";
import { DefaultButton } from "../../../../../common/components/button/defaultButton/DefaultButton";
import { IconButton } from "../../../../../common/components/button/iconButton/IconButton";
import { AppColorPicker } from "../../../../../common/components/forms/inputs/colorPicker/AppColorPicker";
import { DefaultToggle } from "../../../../../common/components/forms/inputs/toggle/DefaultToggle";
import { IconType } from "../../../../../common/icons/Icon";
import { getLeadingEmoji, getMonogram } from "../../../../linkPage/utils/linkPageIconUtility";
import { IStudioWizardFormModel } from "../models/studioWizardFormModel";
import { showCustomConfirmDialog } from "../../../../../common/components/dialogs/AppConfirmDialog";
import { copyToClipboard } from "../../../../../common/utilities/clipboardUtility";
import { getSuccessToast } from "../../../../../common/infrastructure/utilities/toastUtility";
import { ReorderDialog } from "../../../../../common/components/ReorderDialog";
import { useStudioLinkPage } from "./hooks/useStudioLinkPage";
import { IStudioLinkPageItem } from "./models/studioLinkPageModels";
import { IStudioLinkPageItemFormModel } from "./models/studioLinkPageItemFormModel";
import { StudioLinkPageItemDialog } from "./StudioLinkPageItemDialog";
import styles from "./StudioLinkPageSection.module.scss";

interface IStudioLinkPageSectionProps {
  studioId: number;
}

export const StudioLinkPageSection = ({
  studioId,
}: IStudioLinkPageSectionProps) => {
  const {
    items,
    publicUrl,
    isLoading,
    isSaving,
    isReordering,
    saveItem,
    deleteItem,
    reorderItems,
  } = useStudioLinkPage(studioId);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<IStudioLinkPageItem | null>(null);
  const [reorderVisible, setReorderVisible] = useState(false);

  // The appearance settings belong to the studio itself, so they ride along on the wizard's own
  // form and are saved with it — this section only renders the fields.
  const { watch, setValue } = useFormContext<IStudioWizardFormModel>();

  const linkPageButtonColor = watch("linkPageButtonColor");
  const usesDefaultButtonColor = !linkPageButtonColor;
  const defaultButtonColor = watch("buttonBackgroundColor");
  // The studio's own domains, so a link back to its portal gets the platform icon rather
  // than the generic globe Google returns for domains it has never crawled.
  const ownDomains = watch("url");

  const toggleDefaultButtonColor = (useDefault: boolean) =>
    // Clearing the color is how "use the default" is expressed all the way down to the database,
    // so switching back seeds the picker with the studio's own button color to edit from.
    setValue("linkPageButtonColor", useDefault ? "" : defaultButtonColor, {
      shouldDirty: true,
    });

  const openCreate = () => {
    setEditing(null);
    setDialogVisible(true);
  };

  const openEdit = (item: IStudioLinkPageItem) => {
    setEditing(item);
    setDialogVisible(true);
  };

  const handleSave = (values: IStudioLinkPageItemFormModel) => {
    saveItem(values, () => setDialogVisible(false));
  };

  const handleCopy = async () => {
    const ok = await copyToClipboard(publicUrl);
    if (ok) {
      getSuccessToast("Адресът беше копиран.");
    }
  };

  const confirmDelete = (item: IStudioLinkPageItem) => {
    showCustomConfirmDialog(
      `Сигурни ли сте, че искате да премахнете „${item.name}“?`,
      () => deleteItem(item.id),
    );
  };

  return (
    <div className={styles.root}>
      <p className={styles.header_hint}>
        Тук можете да създадете страничка с линкове за улеснение (напр. линк
        към платформата, Instagram, Facebook, локация и тн). Тя се достъпва
        от линкът по-долу - можете да го споделяте - Instagram био, facebook
        и др.
      </p>

      <div className={styles.public_link}>
        <span className={styles.public_link_label}>Вашият адрес:</span>
        <button
          type="button"
          className={styles.public_link_value}
          title="Копирай адреса"
          onClick={handleCopy}
          disabled={!publicUrl}
        >
          {publicUrl || "…"}
        </button>
        <IconButton
          iconType={IconType.Copy}
          iconWidth="20px"
          iconHeight="20px"
          tooltip="Копирай адреса"
          onClick={handleCopy}
          disabled={!publicUrl}
        />
      </div>

      <div className={styles.appearance}>
        <span className={styles.appearance_title}>Външен вид</span>

        <DefaultToggle
          name="useDefaultLinkPageButtonColor"
          label="Бутони в цвета на платформата"
          standalone
          checked={usesDefaultButtonColor}
          width="100%"
          onChange={toggleDefaultButtonColor}
          tooltip="По подразбиране бутоните на линк страницата използват цвета на бутоните на обекта."
        />

        {!usesDefaultButtonColor && (
          <AppColorPicker
            name={nameof<IStudioWizardFormModel>((x) => x.linkPageButtonColor)}
            label="Цвят на бутоните"
            width="100%"
          />
        )}

        <DefaultToggle
          name={nameof<IStudioWizardFormModel>((x) => x.linkPageShowAddress)}
          label="Показвай адреса и линк към Google Maps"
          width="100%"
        />

        <DefaultToggle
          name={nameof<IStudioWizardFormModel>((x) => x.linkPageShowPhone)}
          label="Показвай телефоните за връзка"
          width="100%"
          tooltip="Показва телефонните номера от основната информация на обекта, за да могат посетителите да звъннат директно."
        />

        <p className={styles.appearance_hint}>
          Промените по външния вид се запазват с бутона за запис на обекта.
        </p>
      </div>

      <div className={styles.list_header}>
        <span className={styles.list_header_title}>Линкове</span>
        <div className={styles.list_header_actions}>
          {items.length > 1 && (
            <DefaultButton
              label="Подреди"
              design="outline"
              width="auto"
              onClick={() => setReorderVisible(true)}
            />
          )}
          <IconButton
            iconType={IconType.PlusIcon}
            iconWidth="20px"
            iconHeight="20px"
            tooltip="Добави линк"
            onClick={openCreate}
          />
        </div>
      </div>

      {isLoading && items.length === 0 ? (
        <p className={styles.empty}>Зареждане…</p>
      ) : items.length === 0 ? (
        <p className={styles.empty}>Още няма добавени линкове.</p>
      ) : (
        <div className={styles.list}>
          {items.map((item) => (
            <div key={item.id} className={styles.item}>
              <span className={styles.item_icon} aria-hidden="true">
                {item.iconUrl ? (
                  <img
                    className={styles.item_icon_image}
                    src={item.iconUrl}
                    alt=""
                    loading="lazy"
                  />
                ) : (
                  getLeadingEmoji(item.name) ?? getMonogram(item.name)
                )}
              </span>

              <div className={styles.item_details}>
                <span className={styles.item_name}>{item.name}</span>
                <span className={styles.item_url}>{item.url}</span>
              </div>

              <div className={styles.item_actions}>
                <IconButton
                  iconType={IconType.EditIcon}
                  iconWidth="20px"
                  iconHeight="20px"
                  tooltip="Редакция"
                  onClick={() => openEdit(item)}
                />
                <IconButton
                  iconType={IconType.DeleteIcon2}
                  iconWidth="20px"
                  iconHeight="20px"
                  tooltip="Изтрий"
                  onClick={() => confirmDelete(item)}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <StudioLinkPageItemDialog
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        editing={editing}
        isSaving={isSaving}
        ownDomains={ownDomains ?? undefined}
        onSave={handleSave}
      />

      <ReorderDialog
        visible={reorderVisible}
        onHide={() => setReorderVisible(false)}
        title="Подреждане на линкове"
        items={items}
        loading={isReordering}
        onSave={reorderItems}
      />
    </div>
  );
};
